import { useParams } from 'react-router-dom'

import { Box, Stack } from '@mui/material'
import {
  getCodeSetLabelLookup,
  getLabelFromList,
} from '../../../Functions/CodeSetFns'
import { useUserContext } from '../../../Contexts/UserConfig'
import PageSection from '../../../Constants&Theme/PageSection'
import SampleHistory from './SampleHistory'
import SampleResult from './SampleResult'
import SampledContents from './SampledContents'
import { useSampleGet } from '../../../APIs/Hooks/SampleHooks'
import { formatValueDateTime } from '../../../Functions/FormatValues'
import { useMessagePopupContext } from '../../../garvan-react/Components/ModalFeedback/MessagePopup'
import { stackGap } from '../../../Constants&Theme/Spacing'
import { ReadOnlyTextField } from '../../../garvan-react/Components/ReadOnlyTextField'
import { tsSample } from '../../../Typescript/DB_Interface'
import { useSampleContext } from '../../../Contexts/Modules/SampleModule/SampleModule'

function SampleInfo() {
  const { loginConfig } = useUserContext()
  const { sample_id } = useParams()
  const { setMessage } = useMessagePopupContext()
  const {
    storageCodesQuery,
    freezingCodesQuery,
    organCodesQuery,
    euthanasiaCodesQuery,
  } = useSampleContext()
  const sampleQuery = useSampleGet(
    `/getSample.php?SampleID=${sample_id}`,
    !!sample_id,
    {
      onSuccess: (data) => {
        if (!data?.SampleID) {
          setMessage({
            subject: 'Error getting sample',
            message: (data as any)?.message,
            severity: 'warning',
          })
        }
      },
    },
  )
  const sampleLoading = sampleQuery.isLoading
  const sample = sampleQuery.data as tsSample
  const { data: sampleItems, isLoading: sampleItemsLoading } = useSampleGet(
    `/getSampleItems.php?SampleID=${sample_id}`,
    !!sample_id,
    {
      onSuccess: (data) => {
        if (!data?.animals && !data?.lines) {
          setMessage({
            subject: 'Error getting sample items',
            message: (data as any)?.message,
            severity: 'warning',
          })
        }
      },
    },
  )

  return (
    <Box
      sx={{
        maxWidth: 'md',
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <PageSection
        title={'Sample Information'}
        loading={sampleLoading}
      >
        <Stack
          gap={stackGap.col}
          width={1}
        >
          <Stack
            direction={'row'}
            width={1}
            gap={stackGap.row}
          >
            <ReadOnlyTextField
              label="Sample ID"
              value={sample?.SampleID || ''}
              sx={{ flex: 1 }}
            />
            <ReadOnlyTextField
              label="Vessel ID"
              value={sample?.ParentVesselID || ''}
              sx={{ flex: 1 }}
            />
            <ReadOnlyTextField
              label="Run ID"
              value={sample?.SampleRunID || ''}
              sx={{ flex: 1 }}
            />
            <ReadOnlyTextField
              label="Status"
              value={
                getCodeSetLabelLookup(
                  loginConfig,
                  ['SampleStatusID'],
                  sample?.Status,
                ) || ''
              }
              sx={{ flex: 2 }}
            />
          </Stack>
          <Stack
            direction={'row'}
            width={1}
            gap={stackGap.row}
          >
            <ReadOnlyTextField
              label="Type"
              sx={{ flex: 2 }}
              value={
                getCodeSetLabelLookup(
                  loginConfig,
                  ['SampleRunTypeID'],
                  sample?.SampleType,
                ) || ''
              }
            />
            <ReadOnlyTextField
              label="Date Taken"
              value={formatValueDateTime(sample?.DateTaken)}
              sx={{ flex: 1 }}
            />
            <ReadOnlyTextField
              label="Employee"
              value={sample?.EmployeeID || ''}
              sx={{ flex: 1 }}
            />
          </Stack>
          <Stack
            direction={'row'}
            width={1}
            gap={stackGap.row}
          >
            <ReadOnlyTextField
              label="Origin"
              value={
                getCodeSetLabelLookup(
                  loginConfig,
                  ['KV-Holdings'],
                  sample?.OriginHoldingID,
                ) || ''
              }
              fullWidth
            />
            <ReadOnlyTextField
              label="Destination"
              value={
                getCodeSetLabelLookup(
                  loginConfig,
                  ['KV-Facilities'],
                  sample?.DestinationFacilityID,
                ) || ''
              }
              fullWidth
            />
          </Stack>
        </Stack>
      </PageSection>
      {sample?.metadata && sample.metadata.length > 0 && (
        <PageSection
          title={`Storage Details`}
          loading={sampleLoading}
        >
          <Stack
            gap={stackGap.col}
            width={1}
          >
            <Stack
              direction={'row'}
              width={1}
              gap={stackGap.row}
            >
              <ReadOnlyTextField
                label="Organ Type"
                value={getLabelFromList(
                  organCodesQuery.data,
                  sample?.metadata.find((m) => m.MetaKey === 'OrganType')
                    ?.MetaValue ?? '',
                  'CodeValue',
                  'CodeDescription',
                )}
                sx={{ flex: 1 }}
              />
              <ReadOnlyTextField
                label="Storage Media"
                value={getLabelFromList(
                  storageCodesQuery.data,
                  sample?.metadata.find((m) => m.MetaKey === 'StorageMedia')
                    ?.MetaValue ?? '',
                  'CodeValue',
                  'CodeDescription',
                )}
                sx={{ flex: 1 }}
              />
              <ReadOnlyTextField
                label="Freezing Method"
                value={getLabelFromList(
                  freezingCodesQuery.data,
                  sample?.metadata.find((m) => m.MetaKey === 'FreezingMethod')
                    ?.MetaValue ?? '',
                  'CodeValue',
                  'CodeDescription',
                )}
                sx={{ flex: 1 }}
              />
              <ReadOnlyTextField
                label="Euthanasia Method"
                value={getLabelFromList(
                  euthanasiaCodesQuery.data,
                  sample?.metadata.find((m) => m.MetaKey === 'MethodEuthanasia')
                    ?.MetaValue ?? '',
                  'CodeValue',
                  'CodeDescription',
                )}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Stack
              direction={'row'}
              width={1}
              gap={stackGap.row}
            >
              <ReadOnlyTextField
                label="Sample Details"
                sx={{ flex: 2 }}
                value={
                  sample?.metadata.find((m) => m.MetaKey === 'SampleDetails')
                    ?.MetaValue ?? ''
                }
              />
            </Stack>
          </Stack>
        </PageSection>
      )}
      <PageSection
        title={`Sampled ${sampleItems?.lines && sampleItems?.lines?.length > 0 ? 'Lines' : 'Animals'}`}
        //Pass loading state to DataGrid instead
      >
        <SampledContents
          animals={sampleItems?.animals ?? []}
          lines={sampleItems?.lines ?? []}
          loading={sampleItemsLoading}
        />
      </PageSection>
      {sample?.ScreenCollectionID && (
        <PageSection
          title={'Result'}
          loading={sampleLoading}
        >
          <SampleResult sample={sample} />
        </PageSection>
      )}
      <PageSection title={'History'}>
        <SampleHistory
          history={sample?.history ?? []}
          loading={sampleLoading}
        />
      </PageSection>
    </Box>
  )
}

export default SampleInfo
